import $i18n from 'panda-i18n';
import React, { useRef, memo, } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useDrag, useWheel, } from '@use-gesture/react';
import { useIsomorphicLayoutEffect } from 'ahooks';
import { rubberbandIfOutOfBounds } from '@/components/cn-picker/rubberband';
import { bound } from '../../utils/bound';
import { measureCSSLength } from '@/components/cn-picker/measure-css-length';
import { supportsPassive } from '@/components/cn-picker/supports-passive';
import isEqual from 'lodash/isEqual';
var classPrefix = 'cn-ui-m-picker-view';
export var Wheel = memo(function (props) {
    var value = props.value, column = props.column, renderLabel = props.renderLabel;
    function onSelect(val) {
        props.onSelect(val, props.index);
    }
    var _a = useSpring(function () { return ({
        from: { y: 0 },
        config: {
            tension: 400,
            mass: 0.8,
        },
    }); }), y = _a[0].y, api = _a[1];
    var draggingRef = useRef(false);
    var rootRef = useRef(null);
    var itemHeightMeasureRef = useRef(null);
    var itemHeight = useRef(34);
    useIsomorphicLayoutEffect(function () {
        var itemHeightMeasure = itemHeightMeasureRef.current;
        if (!itemHeightMeasure)
            return;
        itemHeight.current = measureCSSLength(window.getComputedStyle(itemHeightMeasure).getPropertyValue('height'));
    });
    useIsomorphicLayoutEffect(function () {
        if (draggingRef.current)
            return;
        if (value === null)
            return;
        var targetIndex = column.findIndex(function (item) { return item.value === value; });
        if (targetIndex < 0)
            return;
        var finalPosition = targetIndex * -itemHeight.current;
        api.start({ y: finalPosition, immediate: y.goal !== finalPosition });
    }, [value, column]);
    useIsomorphicLayoutEffect(function () {
        if (column.length === 0) {
            if (value !== null) {
                onSelect(null);
            }
        }
        else if (!column.some(function (item) { return item.value === value; })) {
            var firstItem = column[0];
            onSelect(firstItem.value);
        }
    }, [column, value]);
    function scrollSelect(index) {
        var finalPosition = index * -itemHeight;
        api.start({ y: finalPosition });
        var item = column[index];
        if (!item)
            return;
        onSelect(item.value);
    }
    var handleGestureState = function (state) {
        var _a = state.direction, direction = _a[1], _b = state.distance, distance = _b[1], _c = state.velocity, velocity = _c[1], _d = state.offset, offset = _d[1], last = state.last;
        return {
            direction: direction,
            distance: distance,
            velocity: velocity,
            offset: offset,
            last: last,
        };
    };
    var handleDrag = function (state) {
        draggingRef.current = true;
        var min = -((column.length - 1) * itemHeight.current);
        var max = 0;
        var _a = handleGestureState(state), direction = _a.direction, last = _a.last, velocity = _a.velocity, offset = _a.offset;
        if (last) {
            draggingRef.current = false;
            var position = offset + velocity * direction * 50;
            var boundNum = bound(position, min, max);
            var targetIndex = -Math.round(boundNum / itemHeight.current);
            scrollSelect(targetIndex);
        }
        else {
            var position = offset;
            api.start({
                y: rubberbandIfOutOfBounds(position, min, max, itemHeight.current * 50, 0.2),
            });
        }
    };
    var handleWheel = function (state) {
        draggingRef.current = true;
        var min = -((column.length - 1) * itemHeight.current);
        var max = 0;
        var _a = handleGestureState(state), direction = _a.direction, last = _a.last, velocity = _a.velocity, distance = _a.distance;
        var whellDir = -direction; // 取反
        var scrollY = y.get();
        if (last) {
            draggingRef.current = false;
            var speed = velocity * whellDir * 50;
            var position = scrollY + distance * whellDir + speed;
            var boundNum = bound(position, min, max);
            var targetIndex = -Math.round(boundNum / itemHeight.current);
            scrollSelect(targetIndex);
        }
        else {
            var position = scrollY + distance * whellDir;
            api.start({
                y: rubberbandIfOutOfBounds(position, min, max, itemHeight.current * 50, 0.2),
            });
        }
    };
    useDrag(function (state) {
        state.event.stopPropagation();
        handleDrag(state);
    }, {
        axis: 'y',
        from: function () { return [0, y.get()]; },
        filterTaps: true,
        pointer: { touch: true },
        target: rootRef,
    });
    useWheel(function (state) {
        state.event.stopPropagation();
        handleWheel(state);
    }, {
        target: props.mouseWheel ? rootRef : undefined,
        axis: 'y',
        from: function () { return [0, y.get()]; },
        preventDefault: true,
        eventOptions: supportsPassive ? { passive: false } : undefined,
    });
    var selectedIndex = null;
    function renderAccessible() {
        if (selectedIndex === null)
            return null;
        var current = column[selectedIndex];
        var previousIndex = selectedIndex - 1;
        var nextIndex = selectedIndex + 1;
        var previous = column[previousIndex];
        var next = column[nextIndex];
        return (React.createElement("div", { className: "".concat(classPrefix, "-column-accessible") },
            React.createElement("div", { className: "".concat(classPrefix, "-column-accessible-current"), role: "button", "aria-label": current
                    ? $i18n.get({
                        id: 'TheCurrentSelectionIscurrentLabel',
                        dm: '当前选择的是：{currentLabel}',
                        ns: 'CnPickerView',
                    }, { currentLabel: current.label })
                    : $i18n.get({
                        id: 'NotCurrentlySelected',
                        dm: '当前未选择',
                        ns: 'CnPickerView',
                    }) }, "-"),
            React.createElement("div", { className: "".concat(classPrefix, "-column-accessible-button"), onClick: function () {
                    if (!previous)
                        return;
                    scrollSelect(previousIndex);
                }, role: previous ? 'button' : 'text', "aria-label": !previous
                    ? $i18n.get({
                        id: 'NoPreviousItem',
                        dm: '没有上一项',
                        ns: 'CnPickerView',
                    })
                    : $i18n.get({
                        id: 'SelectThePreviousItempreviousLabel',
                        dm: '选择上一项：{previousLabel}',
                        ns: 'CnPickerView',
                    }, { previousLabel: previous.label }) }, "-"),
            React.createElement("div", { className: "".concat(classPrefix, "-column-accessible-button"), onClick: function () {
                    if (!next)
                        return;
                    scrollSelect(nextIndex);
                }, role: next ? 'button' : 'text', "aria-label": !next
                    ? $i18n.get({
                        id: 'NoNextItem',
                        dm: '没有下一项',
                        ns: 'CnPickerView',
                    })
                    : $i18n.get({
                        id: 'SelectTheNextItemnextLabel',
                        dm: '选择下一项：{nextLabel}',
                        ns: 'CnPickerView',
                    }, { nextLabel: next.label }) }, "-")));
    }
    return (React.createElement("div", { className: "".concat(classPrefix, "-column") },
        React.createElement("div", { className: "".concat(classPrefix, "-item-height-measure"), ref: itemHeightMeasureRef }),
        React.createElement(animated.div, { ref: rootRef, style: { translateY: y }, className: "".concat(classPrefix, "-column-wheel"), "aria-hidden": true }, column.map(function (item, index) {
            var _a;
            var selected = props.value === item.value;
            if (selected)
                selectedIndex = index;
            function handleClick() {
                draggingRef.current = false;
                scrollSelect(index);
            }
            return (React.createElement("div", { key: (_a = item.key) !== null && _a !== void 0 ? _a : item.value, "data-selected": item.value === value, className: "".concat(classPrefix, "-column-item"), onClick: handleClick, "aria-hidden": !selected, "aria-label": selected ? 'active' : '' },
                React.createElement("div", { className: "".concat(classPrefix, "-column-item-label") }, renderLabel(item))));
        })),
        renderAccessible()));
}, function (prev, next) {
    if (prev.index !== next.index)
        return false;
    if (prev.value !== next.value)
        return false;
    if (prev.onSelect !== next.onSelect)
        return false;
    if (prev.renderLabel !== next.renderLabel)
        return false;
    if (prev.mouseWheel !== next.mouseWheel)
        return false;
    if (!isEqual(prev.column, next.column))
        return false;
    return true;
});
Wheel.displayName = 'Wheel';
